import { useEffect, useMemo, useRef, useState } from "react";
import useAuth from "../../Users/hooks/useAuth";
import Slideshow from "./Slideshow";

const getDateFromVersion = (version) => {
    const numericVersion = Number(version);
    if (!Number.isFinite(numericVersion)) return null;
    const date = new Date(numericVersion * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getFileDate = (file) => {
    const rawDate = file.updated_at || file.updatedAt || file.created_at || file.createdAt;
    if (rawDate) {
        const date = new Date(rawDate);
        if (!Number.isNaN(date.getTime())) return date;
    }

    return getDateFromVersion(file.version);
};

const isSameCalendarDay = (a, b) => {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

const formatDate = (date, { withGenerated = false } = {}) => {
    if (!date) return "—";

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let label;
    if (isSameCalendarDay(date, today)) {
        label = "Today";
    } else if (isSameCalendarDay(date, yesterday)) {
        label = "Yesterday";
    } else {
        label = new Intl.DateTimeFormat(undefined, {
            day: "numeric",
            month: "short",
            year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
        }).format(date);
    }

    return withGenerated ? `Generated ${label}` : label;
};

const normalizeSegments = (file) => {
    const rawSegments = file?.transcript?.segments || [];

    return rawSegments
        .map((segment) => {
            if (typeof segment === "string") return segment.trim();
            if (segment && typeof segment.text === "string") return segment.text.trim();
            return "";
        })
        .filter(Boolean);
};

const segmentCount = (file) => normalizeSegments(file).length;

const versionKey = (fileOrVersion) => String(
    typeof fileOrVersion === "object" ? fileOrVersion?.version : fileOrVersion,
);

const getNarrationCounts = (file, status = {}) => {
    const total = segmentCount(file);
    const selected = Number(status.selected_count ?? status.selectedCount ?? 0);
    const recorded = Number(status.recorded_count ?? status.recordedCount ?? 0);

    return {
        total,
        selected: Number.isFinite(selected) ? selected : 0,
        recorded: Number.isFinite(recorded) ? recorded : 0,
    };
};

const getFirstPresent = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

const getSharedItems = (status = {}) => {
    const candidates = [
        status.shared_recordings,
        status.sharedRecordings,
        status.published_recordings,
        status.publishedRecordings,
        status.community_recordings,
        status.communityRecordings,
        status.shared_items,
        status.sharedItems,
        status.shares,
        status.shared,
        status.published,
        status.public_recording,
        status.publicRecording,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate.filter(Boolean);
        if (candidate && typeof candidate === "object") return [candidate];
    }

    return [];
};

const getShareId = (status = {}, sharedItems = []) => {
    const firstItem = sharedItems[0] || {};

    return getFirstPresent(
        status.shared_recording_id,
        status.sharedRecordingId,
        status.published_recording_id,
        status.publishedRecordingId,
        status.community_recording_id,
        status.communityRecordingId,
        status.public_recording_id,
        status.publicRecordingId,
        firstItem.recording_id,
        firstItem.recordingId,
        firstItem.id,
        firstItem._id,
    );
};

const getShareUrl = (status = {}, sharedItems = []) => {
    const firstItem = sharedItems[0] || {};

    return getFirstPresent(
        status.share_url,
        status.shareUrl,
        status.shared_url,
        status.sharedUrl,
        status.exercise_url,
        status.exerciseUrl,
        status.public_url,
        status.publicUrl,
        firstItem.share_url,
        firstItem.shareUrl,
        firstItem.exercise_url,
        firstItem.exerciseUrl,
        firstItem.public_url,
        firstItem.publicUrl,
    );
};

const getShareDate = (status = {}, sharedItems = []) => {
    const firstItem = sharedItems[0] || {};
    const rawDate = getFirstPresent(
        status.shared_at,
        status.sharedAt,
        status.published_at,
        status.publishedAt,
        status.public_at,
        status.publicAt,
        firstItem.shared_at,
        firstItem.sharedAt,
        firstItem.published_at,
        firstItem.publishedAt,
        firstItem.created_at,
        firstItem.createdAt,
    );

    if (!rawDate) return null;

    const date = new Date(rawDate);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getSharingState = (file, status, narrationState, versionLabel = "This version") => {
    const { total, selected } = getNarrationCounts(file, status);
    const isReady = narrationState.key === "ready";

    if (!status || status.is_loading) {
        return {
            key: "unknown",
            label: "Sharing not checked",
            className: "bg-slate-50 text-slate-500 ring-slate-200",
            detail: "Sharing status will appear after narration status loads.",
            hint: "Refresh narration status to check whether this version has been shared.",
            isShared: false,
        };
    }

    const sharedItems = getSharedItems(status);
    const shareId = getShareId(status, sharedItems);
    const shareUrl = getShareUrl(status, sharedItems);
    const shareDate = getShareDate(status, sharedItems);
    const explicitShared = Boolean(
        status.is_shared ||
        status.isShared ||
        status.shared_to_community ||
        status.sharedToCommunity ||
        status.is_published ||
        status.isPublished ||
        status.published_to_community ||
        status.publishedToCommunity ||
        status.is_public ||
        status.isPublic ||
        status.has_shared_recording ||
        status.hasSharedRecording,
    );
    const sharedCount = Number(
        status.shared_count ??
        status.sharedCount ??
        status.published_count ??
        status.publishedCount ??
        status.public_count ??
        status.publicCount ??
        sharedItems.length,
    );
    const hasSharedRecording = explicitShared || Boolean(shareId || shareUrl) || (Number.isFinite(sharedCount) && sharedCount > 0);

    if (hasSharedRecording) {
        const dateLabel = shareDate ? ` · ${formatDate(shareDate)}` : "";
        const selectedLabel = total ? ` · ${selected}/${total} selected` : "";
        const idLabel = shareId ? ` · ID ${String(shareId).slice(0, 8)}` : "";

        return {
            key: "shared",
            label: "Shared",
            className: "bg-blue-50 text-blue-700 ring-blue-200",
            detail: `Shared to community: ${versionLabel}${selectedLabel}${dateLabel}${idLabel}`,
            hint: shareUrl || "This transcript version has a shared narration.",
            isShared: true,
            shareUrl,
            shareId,
        };
    }

    if (!isReady) {
        return {
            key: "disabled",
            label: "Not shared",
            className: "bg-slate-100 text-slate-600 ring-slate-200",
            detail: total ? `Sharing is disabled until all ${total} narrations are selected.` : "Add transcript segments before sharing.",
            hint: "Finish narration before sharing this version.",
            isShared: false,
        };
    }

    return {
        key: "ready-to-share",
        label: "Not shared",
        className: "bg-purple-50 text-purple-700 ring-purple-200",
        detail: `Ready to share: ${versionLabel}. Open Review / Share narration to publish it.`,
        hint: "Open narration review to share this completed version.",
        isShared: false,
    };
};

const getNarrationState = (file, status) => {
    const { total, selected, recorded } = getNarrationCounts(file, status);

    if (!total) {
        return {
            key: "empty",
            label: "No segments",
            action: "Narrate text",
            className: "bg-slate-50 text-slate-500 ring-slate-200",
            hint: "Add transcript segments first.",
        };
    }

    if (!status || status.is_loading) {
        return {
            key: "unknown",
            label: "Narration not checked",
            action: "Narrate text",
            className: "bg-slate-50 text-slate-500 ring-slate-200",
            hint: "Open narration to record this text.",
        };
    }

    if (selected >= total) {
        return {
            key: "ready",
            label: `Narrated · ${selected}/${total}`,
            action: "Review / Share narration",
            className: "bg-green-50 text-green-700 ring-green-200",
            hint: "Full narration is ready. Review selected takes, share to community, or improve recordings.",
        };
    }

    if (recorded > 0 || selected > 0) {
        return {
            key: "partial",
            label: `Missing narration · ${selected}/${total}`,
            action: "Continue narration",
            className: "bg-amber-50 text-amber-700 ring-amber-200",
            hint: "Some sentences still need selected narration.",
        };
    }

    return {
        key: "not-started",
        label: `Not narrated · 0/${total}`,
        action: "Narrate text",
        className: "bg-slate-100 text-slate-600 ring-slate-200",
        hint: "No sentence narration has been selected yet.",
    };
};

const getVersionActionLabel = (narrationState, sharingState) => {
    if (narrationState.key !== "ready") return narrationState.action;
    if (sharingState?.isShared) return "Open / manage sharing";
    return "Review / Share narration";
};

const createSegmentId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }

    return `segment-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createEditableSegment = (text = "") => ({
    id: createSegmentId(),
    text,
});

const normalizeEditableSegments = (file) => normalizeSegments(file).map((segment) => createEditableSegment(segment));

const getVersionLabel = (index, total) => {
    if (index === 0) return "Latest version";
    if (index === 1) return "Previous version";
    return `Older version ${index - 1}`;
};

const TranscriptEditorOverlay = ({ file, presentationTitle, onClose, onSaved, fetchWithAuth }) => {
    const [segments, setSegments] = useState(() => normalizeEditableSegments(file));
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");
    const [pendingFocusId, setPendingFocusId] = useState(null);
    const textareaRefs = useRef({});
    const saveInFlightRef = useRef(false);
    const editRequestIdRef = useRef(createSegmentId());

    const cleanedSegments = useMemo(
        () => segments.map((segment) => segment.text.trim()).filter(Boolean),
        [segments],
    );

    const canSave = cleanedSegments.length > 0 && !isSaving;

    useEffect(() => {
        if (!pendingFocusId) return;

        const target = textareaRefs.current[pendingFocusId];
        if (target) {
            target.focus();
            const valueLength = target.value.length;
            target.setSelectionRange(valueLength, valueLength);
            setPendingFocusId(null);
        }
    }, [pendingFocusId, segments]);

    const updateSegment = (id, value) => {
        setSegments((prev) => prev.map((segment) => (segment.id === id ? { ...segment, text: value } : segment)));
    };

    const addSegmentAfter = (id) => {
        const newSegment = createEditableSegment("");

        setSegments((prev) => {
            const index = prev.findIndex((segment) => segment.id === id);
            if (index === -1) return prev;

            const next = [...prev];
            next.splice(index + 1, 0, newSegment);
            return next;
        });

        setPendingFocusId(newSegment.id);
    };

    const deleteSegment = (id) => {
        setSegments((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((segment) => segment.id !== id);
        });
    };

    const mergeWithNext = (id) => {
        setSegments((prev) => {
            const index = prev.findIndex((segment) => segment.id === id);
            if (index === -1 || index >= prev.length - 1) return prev;

            const current = prev[index];
            const nextSegment = prev[index + 1];
            const mergedText = [current.text.trim(), nextSegment.text.trim()].filter(Boolean).join(" ").trim();

            const next = [...prev];
            next.splice(index, 2, { ...current, text: mergedText });
            return next;
        });
    };

    const saveEditedTranscript = async () => {
        // Prevent duplicate saves from double-clicks, slow renders, or repeated key activation.
        if (!canSave || saveInFlightRef.current) return;

        saveInFlightRef.current = true;
        setIsSaving(true);
        setError("");

        try {
            await fetchWithAuth("upload-text", "POST", {
                title: presentationTitle.title,
                language: presentationTitle.language,
                text: cleanedSegments.join("\n\n"),
                segments: cleanedSegments,
                edited: true,
                source_version: file.version,
                edit_request_id: editRequestIdRef.current,
            });

            await onSaved();
            onClose();
        } catch (requestError) {
            console.error("Failed to save edited transcript:", requestError);
            setError("We could not save the edited transcript. Please try again.");
            saveInFlightRef.current = false;
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-md">
            <div className="flex h-[calc(100vh-3rem)] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_30px_100px_rgba(15,23,42,0.28)] ring-1 ring-slate-200">
                <header className="shrink-0 border-b border-blue-100/80 bg-white/95 px-6 py-5 shadow-sm backdrop-blur-xl">
                    <div className="flex items-start justify-between gap-6">
                        <div className="min-w-0">
                            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.20em] text-blue-600">Transcript editor</p>
                            <h2 className="truncate text-2xl font-bold tracking-tight text-slate-950">
                                {presentationTitle.title}
                            </h2>
                            <p className="mt-1 text-sm leading-6 text-slate-500">
                                Rewrite whole segments, add missing sentences, or merge short fragments. Saving creates a new version.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            aria-label="Close transcript editor"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </header>

                <main className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-blue-100 bg-blue-50/50 px-5 py-4">
                        <div>
                            <p className="text-sm font-semibold text-slate-950">Editing source version</p>
                            <p className="mt-1 text-sm text-slate-500">
                                {formatDate(getFileDate(file), { withGenerated: true })} · {segmentCount(file)} segments
                            </p>
                        </div>
                        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                            New version will be created
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
                            <p className="text-sm font-medium text-red-700">{error}</p>
                        </div>
                    )}

                    <div className="space-y-4">
                        {segments.map((segment, index) => (
                            <section key={segment.id} className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-slate-100 px-2 text-sm font-bold text-slate-700">
                                            {index + 1}
                                        </span>
                                        <p className="text-sm font-semibold text-slate-950">Segment {index + 1}</p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => addSegmentAfter(segment.id)}
                                            className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            Add after
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => mergeWithNext(segment.id)}
                                            disabled={index >= segments.length - 1}
                                            className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Merge next
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteSegment(segment.id)}
                                            disabled={segments.length <= 1}
                                            className="inline-flex h-9 items-center justify-center rounded-xl px-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                <textarea
                                    ref={(node) => {
                                        if (node) {
                                            textareaRefs.current[segment.id] = node;
                                        } else {
                                            delete textareaRefs.current[segment.id];
                                        }
                                    }}
                                    className="min-h-[112px] w-full resize-y rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base leading-7 text-slate-950 shadow-inner transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                                    value={segment.text}
                                    onChange={(event) => updateSegment(segment.id, event.target.value)}
                                    placeholder="Write this segment here..."
                                />
                            </section>
                        ))}
                    </div>
                </main>

                <footer className="shrink-0 border-t border-slate-100 bg-white px-6 py-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-slate-500">
                            {cleanedSegments.length} saved segment{cleanedSegments.length === 1 ? "" : "s"}
                        </p>
                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveEditedTranscript}
                                disabled={!canSave}
                                className="inline-flex h-11 items-center justify-center rounded-2xl bg-blue-600 px-6 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                            >
                                {isSaving ? "Saving new version..." : "Save as new version"}
                            </button>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};


const DeleteVersionDialog = ({
    file,
    sharingState,
    onCancel,
    onConfirmDelete,
    isDeleting,
    deletingMode,
}) => {
    if (!file) return null;

    const count = segmentCount(file);
    const generatedLabel = formatDate(getFileDate(file), { withGenerated: true });
    const isShared = Boolean(sharingState?.isShared);
    const sharedTarget = sharingState?.shareId || sharingState?.shareUrl || "";

    return (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-slate-950/40 px-4 py-6 backdrop-blur-md">
            <div className="w-full max-w-lg rounded-[28px] bg-white p-6 shadow-[0_30px_100px_rgba(15,23,42,0.28)] ring-1 ring-slate-200">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    </svg>
                </div>

                <h2 className="text-xl font-bold tracking-tight text-slate-950">
                    {isShared ? "Delete shared transcript version?" : "Delete this transcript version?"}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                    {isShared
                        ? "This version is shared to the community. Deleting it will also unshare the public exercise."
                        : "This removes this saved transcript version. The narration text itself and other versions will stay."}
                </p>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-sm font-semibold text-slate-950">{generatedLabel}</p>
                    <p className="mt-1 text-sm text-slate-500">
                        {count} segment{count === 1 ? "" : "s"}
                        {file.edited ? " · edited" : ""}
                    </p>
                    {isShared && (
                        <p className="mt-2 break-all rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
                            Shared exercise: {sharedTarget || "linked community recording"}
                        </p>
                    )}
                </div>

                <p className="mt-4 text-sm font-medium text-red-600">This cannot be undone.</p>

                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isDeleting}
                        className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={onConfirmDelete}
                        disabled={isDeleting}
                        className="inline-flex h-11 items-center justify-center rounded-2xl bg-red-600 px-5 text-sm font-semibold text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                    >
                        {isDeleting
                            ? deletingMode === "delete-and-unshare"
                                ? "Unsharing and deleting..."
                                : "Deleting..."
                            : isShared
                                ? "Unshare and delete"
                                : "Delete version"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const PresentationTitle = ({ presentation_title, successHandler }) => {
    const { setAuth } = useAuth();
    const [listFiles, setListFiles] = useState([]);
    const [textInput, setTextInput] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [editingFile, setEditingFile] = useState(null);
    const [slideshow, setSlideshow] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [error, setError] = useState("");
    const [versionToDelete, setVersionToDelete] = useState(null);
    const [isDeletingVersion, setIsDeletingVersion] = useState(false);
    const [deletingVersionMode, setDeletingVersionMode] = useState("");
    const [recordingStatusByVersion, setRecordingStatusByVersion] = useState({});
    const [localShareStatusByVersion, setLocalShareStatusByVersion] = useState({});
    const [isLoadingRecordingStatus, setIsLoadingRecordingStatus] = useState(false);

    const [savedDescription, setSavedDescription] = useState(() =>
        String(presentation_title?.description || "").trim(),
    );
    const [descriptionInput, setDescriptionInput] = useState(() =>
        String(presentation_title?.description || "").trim(),
    );
    const [isSavingDescription, setIsSavingDescription] = useState(false);
    const [descriptionSavedMessage, setDescriptionSavedMessage] = useState("");
    const [isDescriptionEditorOpen, setIsDescriptionEditorOpen] = useState(false);
    const [isLoadingDescription, setIsLoadingDescription] = useState(false);
    const descriptionRequestIdRef = useRef(0);

    const sortedFiles = useMemo(() => {
        return [...listFiles].sort((a, b) => Number(b.version || 0) - Number(a.version || 0));
    }, [listFiles]);

    const latestFile = sortedFiles[0] || null;
    const latestDate = latestFile ? getFileDate(latestFile) : null;
    const hasVersions = sortedFiles.length > 0;
    const cleanSourceText = textInput.trim();
    const canSaveText = cleanSourceText.length >= 10 && !isSaving;
    const cleanDescription = descriptionInput.trim();
    const canSaveDescription = cleanDescription !== savedDescription && !isSavingDescription;

    const fetchWithAuth = async (route, method, outdata) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;

        try {
            const response = await fetch(url, {
                method,
                body: method === "GET" ? undefined : JSON.stringify(outdata),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }

            if (!response.ok) {
                throw new Error(`Request failed with status: ${response.status}`);
            }

            return await response.json();
        } catch (requestError) {
            console.error("Presentation request failed:", requestError);
            throw requestError;
        }
    };

    const refreshRecordingStatuses = async (files = listFiles) => {
        const versions = Array.isArray(files) ? files.filter((file) => file?.version != null) : [];

        if (!versions.length) {
            setRecordingStatusByVersion({});
            return;
        }

        setIsLoadingRecordingStatus(true);

        try {
            const token = localStorage.getItem("token");
            const entries = await Promise.all(
                versions.map(async (file) => {
                    const url = `${process.env.REACT_APP_USER_API_URL}/presentation/presentation_recordings/${encodeURIComponent(String(file.language || presentation_title.language || ""))}/${encodeURIComponent(String(file.title || presentation_title.title || ""))}/${encodeURIComponent(String(file.version))}`;

                    try {
                        const response = await fetch(url, {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        });

                        if (!response.ok) {
                            throw new Error(`Recording status failed: ${response.status}`);
                        }

                        const payload = await response.json();

                        return [
                            versionKey(file),
                            {
                                ...payload,
                                is_loading: false,
                            },
                        ];
                    } catch (statusError) {
                        console.error("Could not load narration status:", statusError);

                        return [
                            versionKey(file),
                            {
                                recorded_count: 0,
                                selected_count: 0,
                                is_loading: false,
                                error: true,
                            },
                        ];
                    }
                }),
            );

            setRecordingStatusByVersion(Object.fromEntries(entries));
        } finally {
            setIsLoadingRecordingStatus(false);
        }
    };

    const getFiles = async () => {
        setIsLoading(true);
        setError("");

        try {
            const json = await fetchWithAuth("list-texts", "POST", presentation_title);
            if (json) {
                setListFiles(json);
                refreshRecordingStatuses(json);
            }
        } catch (requestError) {
            console.error("Failed to fetch transcript versions:", requestError);
            setError("We could not load transcript versions. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const loadPresentationDescription = async () => {
        if (!presentation_title?.title || !presentation_title?.language) return;

        const requestId = descriptionRequestIdRef.current + 1;
        descriptionRequestIdRef.current = requestId;

        setIsLoadingDescription(true);

        try {
            const json = await fetchWithAuth("get-description", "POST", {
                title: presentation_title.title,
                language: presentation_title.language,
            });

            if (!json || descriptionRequestIdRef.current !== requestId) return;

            const nextDescription = String(json.description || "").trim();

            setSavedDescription(nextDescription);
            setDescriptionInput(nextDescription);
            setDescriptionSavedMessage("");
            setIsDescriptionEditorOpen(false);
        } catch (requestError) {
            console.error("Failed to load presentation description:", requestError);
            // Do not show a blocking page error here. The transcript editor still works.
        } finally {
            if (descriptionRequestIdRef.current === requestId) {
                setIsLoadingDescription(false);
            }
        }
    };

    useEffect(() => {
        getFiles();
        loadPresentationDescription();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presentation_title?.title, presentation_title?.language]);

    useEffect(() => {
        const nextDescription = String(presentation_title?.description || "").trim();
        setSavedDescription(nextDescription);
        setDescriptionInput(nextDescription);
        setDescriptionSavedMessage("");
        setIsDescriptionEditorOpen(false);
    }, [presentation_title?.title, presentation_title?.language, presentation_title?.description]);

    const saveDescription = async () => {
        if (!canSaveDescription) return;

        setIsSavingDescription(true);
        setDescriptionSavedMessage("");
        setError("");

        try {
            const json = await fetchWithAuth("update-description", "POST", {
                title: presentation_title.title,
                language: presentation_title.language,
                description: cleanDescription,
            });

            const nextDescription = String(json?.description ?? cleanDescription).trim();
            setSavedDescription(nextDescription);
            setDescriptionInput(nextDescription);
            setDescriptionSavedMessage(nextDescription ? "Description saved." : "Description cleared.");
            setIsDescriptionEditorOpen(false);
        } catch (requestError) {
            console.error("Failed to save presentation description:", requestError);
            setError("We could not save the presentation description. Please try again.");
        } finally {
            setIsSavingDescription(false);
        }
    };

    const saveSourceText = async () => {
        if (isSaving) return;

        if (cleanSourceText.length < 10) {
            setError("Please add at least 10 characters before generating a transcript.");
            return;
        }

        setIsSaving(true);
        setError("");

        try {
            await fetchWithAuth("upload-text", "POST", {
                title: presentation_title.title,
                language: presentation_title.language,
                text: cleanSourceText,
            });

            setTextInput("");
            await getFiles();
        } catch (requestError) {
            console.error("Failed to save source text:", requestError);
            setError("We could not save and generate this version. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const openEditor = (file) => {
        setSelectedFile(file);
        setEditingFile(file);
        setIsEditorOpen(true);
    };

    const closeEditor = () => {
        setIsEditorOpen(false);
        setEditingFile(null);
    };

    const openPractice = (file) => {
        setSlideshow({
            transcript: file.transcript,
            version: file.version,
            title: file.title,
            language: file.language,
            description: savedDescription,
        });
    };

    const closeSlideshow = () => {
        setSlideshow(null);
        refreshRecordingStatuses(sortedFiles);
    };

    const mergeRecordingAndShareStatus = (file, status = {}) => ({
        ...(status || {}),
        ...(localShareStatusByVersion[versionKey(file)] || {}),
    });

    const getVersionSharingState = (file) => {
        if (!file) return null;

        const rawStatus = recordingStatusByVersion[versionKey(file)];
        const mergedStatus = mergeRecordingAndShareStatus(file, rawStatus);
        const narrationState = getNarrationState(file, mergedStatus);
        const fileIndex = sortedFiles.findIndex((item) => versionKey(item) === versionKey(file));
        const versionLabel = fileIndex >= 0
            ? getVersionLabel(fileIndex, sortedFiles.length)
            : "This version";

        return getSharingState(file, mergedStatus, narrationState, versionLabel);
    };

    const getSharedRecordingsExceptVersion = (currentVersion) => {
        const currentKey = versionKey(currentVersion);

        return sortedFiles
            .map((file, index) => {
                if (versionKey(file) === currentKey) return null;

                const sharingState = getVersionSharingState(file);
                if (!sharingState?.isShared) return null;

                return {
                    title: file.title || presentation_title.title,
                    language: file.language || presentation_title.language,
                    version: file.version,
                    version_label: getVersionLabel(index, sortedFiles.length),
                    recording_id: sharingState.shareId || "",
                    share_url: sharingState.shareUrl || "",
                };
            })
            .filter(Boolean);
    };

    const markVersionAsUnshared = (targetVersion) => {
        const key = versionKey(targetVersion);

        if (!key) return;

        setLocalShareStatusByVersion((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] || {}),
                is_shared: false,
                is_published: false,
                shared_to_community: false,
                published_to_community: false,
                is_public: false,
                has_shared_recording: false,
                shared_recording_id: "",
                published_recording_id: "",
                community_recording_id: "",
                public_recording_id: "",
                share_url: "",
                shared_url: "",
                exercise_url: "",
                public_url: "",
                shared_recordings: [],
                published_recordings: [],
                community_recordings: [],
                shared_items: [],
                shares: [],
                shared_count: 0,
                published_count: 0,
                public_count: 0,
                unshared_at: new Date().toISOString(),
            },
        }));
    };

    const handleUnsharedRecording = ({ version: unsharedVersion } = {}) => {
        markVersionAsUnshared(unsharedVersion);
    };

    const handleSharedRecording = ({ version: sharedVersion, shareInfo, unsharedVersions = [] } = {}) => {
        const key = versionKey(sharedVersion ?? slideshow?.version);

        if (!key || !shareInfo) return;

        const now = new Date().toISOString();
        const recordingId = shareInfo.recording_id || shareInfo.recordingId || shareInfo.id || shareInfo._id || "";
        const shareUrl = shareInfo.exercise_url || shareInfo.share_url || shareInfo.public_url || "";
        const itemCount = Number(shareInfo.item_count ?? shareInfo.itemCount ?? 0);
        const totalSegments = Number(shareInfo.total_segments ?? shareInfo.totalSegments ?? 0);

        setLocalShareStatusByVersion((prev) => {
            const next = { ...prev };
            const versionsToUnshare = new Set([
                ...unsharedVersions.map((item) => versionKey(item)),
                ...getSharedRecordingsExceptVersion(sharedVersion).map((item) => versionKey(item.version)),
            ]);

            versionsToUnshare.forEach((unsharedKey) => {
                if (!unsharedKey || unsharedKey === key) return;

                next[unsharedKey] = {
                    ...(next[unsharedKey] || {}),
                    is_shared: false,
                    is_published: false,
                    shared_to_community: false,
                    published_to_community: false,
                    is_public: false,
                    has_shared_recording: false,
                    shared_recording_id: "",
                    published_recording_id: "",
                    community_recording_id: "",
                    public_recording_id: "",
                    share_url: "",
                    shared_url: "",
                    exercise_url: "",
                    public_url: "",
                    shared_recordings: [],
                    published_recordings: [],
                    community_recordings: [],
                    shared_items: [],
                    shares: [],
                    shared_count: 0,
                    published_count: 0,
                    public_count: 0,
                    unshared_at: now,
                };
            });

            next[key] = {
                ...(next[key] || {}),
                is_shared: true,
                is_published: true,
                shared_to_community: true,
                shared_recording_id: recordingId,
                published_recording_id: recordingId,
                share_url: shareUrl,
                exercise_url: shareUrl,
                public_url: shareUrl,
                shared_at: now,
                published_at: now,
                shared_count: Number.isFinite(itemCount) ? itemCount : 0,
                total_segments: Number.isFinite(totalSegments) ? totalSegments : 0,
            };

            return next;
        });
    };

    const requestDeleteVersion = (file) => {
        setVersionToDelete(file);
        setError("");
    };

    const cancelDeleteVersion = () => {
        if (isDeletingVersion) return;
        setVersionToDelete(null);
        setDeletingVersionMode("");
    };

    const unshareVersion = async (file, sharingState) => {
        const payload = {
            title: presentation_title.title,
            language: presentation_title.language,
            version: file.version,
            recording_id: sharingState?.shareId || "",
            share_url: sharingState?.shareUrl || "",
        };

        await fetchWithAuth("unshare-recording", "POST", payload);
    };

    const confirmDeleteVersion = async (mode = "transcript-only") => {
        if (!versionToDelete || isDeletingVersion) return;

        const sharingState = getVersionSharingState(versionToDelete);
        const shouldUnshare = mode === "delete-and-unshare" && sharingState?.isShared;

        setIsDeletingVersion(true);
        setDeletingVersionMode(mode);
        setError("");

        try {
            if (shouldUnshare) {
                await unshareVersion(versionToDelete, sharingState);
            }

            await fetchWithAuth("delete-text-version", "POST", {
                title: presentation_title.title,
                language: presentation_title.language,
                version: versionToDelete.version,
            });

            if (selectedFile?.version === versionToDelete.version) {
                setSelectedFile(null);
            }

            if (editingFile?.version === versionToDelete.version) {
                closeEditor();
            }

            if (shouldUnshare) {
                setLocalShareStatusByVersion((prev) => {
                    const next = { ...prev };
                    delete next[versionKey(versionToDelete)];
                    return next;
                });
            }

            setVersionToDelete(null);
            setDeletingVersionMode("");
            await getFiles();
        } catch (requestError) {
            console.error("Failed to delete transcript version:", requestError);
            setError(
                shouldUnshare
                    ? "We could not unshare and delete this version. Nothing was deleted. Please try again."
                    : "We could not delete this transcript version. Please try again.",
            );
        } finally {
            setIsDeletingVersion(false);
            setDeletingVersionMode("");
        }
    };

    const renderTranscriptInputSection = () => (
        <section className="relative overflow-hidden rounded-[34px] border border-emerald-100 bg-white shadow-[0_24px_70px_-46px_rgba(16,185,129,0.72)] ring-1 ring-emerald-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-400 to-blue-500" />
            <div className="border-b border-emerald-100 bg-gradient-to-br from-white via-emerald-50/70 to-cyan-50/50 px-5 py-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <div className="mb-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                            {hasVersions ? "New version" : "First transcript"}
                        </div>
                        <h2 className="text-xl font-bold tracking-tight text-slate-950">
                            {hasVersions ? "Add new transcript version" : "Add first transcript"}
                        </h2>
                        <p className="mt-1 text-sm leading-6 text-slate-500">
                            Paste the source text. Saving prepares sentence segments for recording.
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-gradient-to-br from-white via-emerald-50/15 to-white px-5 py-5">
                <textarea
                    className="min-h-[220px] w-full resize-y rounded-3xl border border-slate-200 bg-white px-5 py-4 text-base leading-7 text-slate-950 shadow-inner transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                    placeholder={`Paste ${String(presentation_title.language || "").toLowerCase()} text here...`}
                    value={textInput}
                    onChange={(event) => {
                        setTextInput(event.target.value);
                        if (error) setError("");
                    }}
                />

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-3">
                        <p className={`text-sm ${cleanSourceText.length > 0 && cleanSourceText.length < 10 ? "text-red-500" : "text-slate-500"}`}>
                            {textInput.length} character{textInput.length === 1 ? "" : "s"}
                        </p>

                        {textInput.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setTextInput("")}
                                disabled={isSaving}
                                className="text-sm font-semibold text-slate-500 underline-offset-4 transition hover:text-slate-800 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Clear text
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={saveSourceText}
                        disabled={!canSaveText}
                        className="inline-flex h-12 items-center justify-center rounded-2xl bg-blue-600 px-7 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                    >
                        {isSaving
                            ? "Generating transcript..."
                            : hasVersions
                                ? "Save as new transcript version"
                                : "Save first transcript"}
                    </button>
                </div>
            </div>
        </section>
    );

    const renderDescriptionSection = () => (
        <section className="relative overflow-hidden rounded-[34px] border border-amber-100 bg-white shadow-[0_24px_70px_-46px_rgba(245,158,11,0.70)] ring-1 ring-amber-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-400 via-orange-300 to-blue-400" />
            <div className="flex flex-col gap-3 border-b border-amber-100 bg-gradient-to-br from-white via-amber-50/70 to-orange-50/40 px-5 py-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="mb-2 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                        Optional note
                    </div>
                    <h2 className="text-xl font-bold tracking-tight text-slate-950">About this narration text</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                        Add a source note, learning goal, level, dialect note, or teacher instruction.
                        {isLoadingDescription ? " Loading saved description..." : ""}
                    </p>
                </div>

                {!isDescriptionEditorOpen && (
                    <button
                        type="button"
                        onClick={() => {
                            setDescriptionInput(savedDescription);
                            setDescriptionSavedMessage("");
                            setIsDescriptionEditorOpen(true);
                        }}
                        disabled={isLoadingDescription}
                        className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isLoadingDescription
                            ? "Loading..."
                            : savedDescription
                                ? "Update description"
                                : "Add description"}
                    </button>
                )}
            </div>

            <div className="bg-gradient-to-br from-white via-amber-50/15 to-white px-5 py-5">
                {isDescriptionEditorOpen ? (
                    <>
                        <textarea
                            className="min-h-[120px] w-full resize-y rounded-3xl border border-slate-200 bg-white px-5 py-4 text-base leading-7 text-slate-950 shadow-inner transition placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100"
                            placeholder="Optional description, source note, learning goal, dialect note, or teacher instruction..."
                            value={descriptionInput}
                            onChange={(event) => {
                                setDescriptionInput(event.target.value);
                                setDescriptionSavedMessage("");
                            }}
                        />

                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500">
                                    {descriptionInput.length} character{descriptionInput.length === 1 ? "" : "s"}
                                </p>

                                {descriptionSavedMessage && (
                                    <p className="text-sm font-semibold text-green-600">
                                        {descriptionSavedMessage}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-wrap gap-2 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDescriptionInput(savedDescription);
                                        setDescriptionSavedMessage("");
                                        setIsDescriptionEditorOpen(false);
                                    }}
                                    disabled={isSavingDescription}
                                    className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={saveDescription}
                                    disabled={!canSaveDescription || isLoadingDescription}
                                    className={`inline-flex h-11 min-w-[160px] items-center justify-center rounded-2xl px-6 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                        !canSaveDescription || isLoadingDescription
                                            ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-500 shadow-none"
                                            : "bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
                                    }`}
                                >
                                    {isSavingDescription
                                        ? "Saving..."
                                        : !canSaveDescription
                                            ? "No changes"
                                            : "Save description"}
                                </button>
                            </div>
                        </div>
                    </>
                ) : isLoadingDescription ? (
                    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50/70 px-5 py-6">
                        <p className="font-semibold text-slate-700">Loading saved description...</p>
                        <p className="mt-1 text-sm leading-6 text-slate-500">
                            Checking the presentation record in the database.
                        </p>
                    </div>
                ) : savedDescription ? (
                    <div className="rounded-3xl border border-slate-200 bg-slate-50/70 px-5 py-4">
                        <p className="whitespace-pre-wrap text-base leading-7 text-slate-800">
                            {savedDescription}
                        </p>

                        {descriptionSavedMessage && (
                            <p className="mt-3 text-sm font-semibold text-green-600">
                                {descriptionSavedMessage}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50/70 px-5 py-6">
                        <p className="font-semibold text-slate-700">No description yet.</p>
                        <p className="mt-1 text-sm leading-6 text-slate-500">
                            Add one if learners need context before they start recording.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );

    const renderSavedVersionsSection = () => (
        <section className="relative overflow-hidden rounded-[34px] border border-blue-100 bg-white shadow-[0_24px_70px_-46px_rgba(37,99,235,0.72)] ring-1 ring-blue-50">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-sky-400 to-indigo-500" />
            <div className="flex flex-col gap-3 border-b border-blue-100 bg-gradient-to-br from-white via-blue-50/60 to-indigo-50/30 px-5 py-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h2 className="text-xl font-bold tracking-tight text-slate-950">Available transcript versions</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-500">
                        Edit transcripts, continue narration, review sharing status, or delete old versions.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={getFiles}
                    disabled={isLoading}
                    className="inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Refresh
                </button>
            </div>

            <div className="space-y-4 bg-gradient-to-br from-white via-blue-50/20 to-white p-5">
                {isLoading ? (
                    <div className="rounded-3xl border border-dashed border-slate-200 px-5 py-12 text-center text-sm text-slate-500">
                        Loading versions...
                    </div>
                ) : !hasVersions ? (
                    <div className="rounded-3xl border border-dashed border-slate-200 px-5 py-12 text-center">
                        <p className="font-semibold text-slate-950">No transcript yet</p>
                        <p className="mt-1 text-sm text-slate-500">Add source text to prepare the first recording transcript.</p>
                    </div>
                ) : (
                    sortedFiles.map((file, index) => {
                        const isSelected = selectedFile?.version === file.version;
                        const count = segmentCount(file);
                        const rawNarrationStatus = recordingStatusByVersion[versionKey(file)];
                        const narrationStatus = mergeRecordingAndShareStatus(file, rawNarrationStatus);
                        const narrationState = getNarrationState(file, narrationStatus);
                        const versionLabel = getVersionLabel(index, sortedFiles.length);
                        const sharingState = getSharingState(file, narrationStatus, narrationState, versionLabel);
                        const actionLabel = getVersionActionLabel(narrationState, sharingState);

                        return (
                            <article
                                key={`${file.title}-${file.language}-${file.version}`}
                                className={`rounded-[28px] border bg-white p-5 shadow-sm transition ${
                                    isSelected
                                        ? "border-blue-300 ring-4 ring-blue-100"
                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/60"
                                }`}
                            >
                                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="font-bold text-slate-950">{versionLabel}</h3>
                                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-200">
                                                Ready
                                            </span>
                                            {file.edited && (
                                                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-amber-200">
                                                    Edited
                                                </span>
                                            )}
                                            <span
                                                className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${narrationState.className}`}
                                                title={sharingState.key === "ready-to-share" || sharingState.isShared ? sharingState.hint : narrationState.hint}
                                            >
                                                {isLoadingRecordingStatus && !rawNarrationStatus ? "Checking narration..." : narrationState.label}
                                            </span>
                                            <span
                                                className={`rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${sharingState.className}`}
                                                title={sharingState.hint}
                                            >
                                                {isLoadingRecordingStatus && !rawNarrationStatus ? "Checking sharing..." : sharingState.label}
                                            </span>
                                        </div>
                                        <p className="mt-2 text-sm leading-6 text-slate-500">
                                            {formatDate(getFileDate(file), { withGenerated: true })} · {count} segment{count === 1 ? "" : "s"}
                                            {narrationStatus && (
                                                <> · {getNarrationCounts(file, narrationStatus).selected}/{count} selected narration{count === 1 ? "" : "s"}</>
                                            )}
                                        </p>
                                        <p className={`mt-1 text-sm leading-6 ${
                                            sharingState.isShared ? "font-semibold text-blue-700" : "text-slate-500"
                                        }`}>
                                            {sharingState.detail}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[auto_auto_auto] lg:justify-end">
                                        <button
                                            type="button"
                                            onClick={() => openEditor(file)}
                                            className={`inline-flex h-11 min-w-[150px] items-center justify-center rounded-2xl px-5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                                isSelected
                                                    ? "bg-blue-50 text-blue-700"
                                                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                            }`}
                                        >
                                            Edit transcript
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openPractice(file)}
                                            className={`inline-flex h-11 min-w-[210px] items-center justify-center rounded-2xl border px-5 text-sm font-black shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                                narrationState.key === "ready"
                                                    ? "border-emerald-200 bg-emerald-50 text-emerald-800 shadow-emerald-600/10 hover:border-emerald-300 hover:bg-emerald-100 focus:ring-emerald-500"
                                                    : "border-blue-600 bg-blue-600 text-white shadow-blue-600/20 hover:bg-blue-700 focus:ring-blue-500"
                                            }`}
                                            title={narrationState.hint}
                                        >
                                            <span className={narrationState.key === "ready" ? "text-emerald-800" : "text-white"}>
                                                {actionLabel}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => requestDeleteVersion(file)}
                                            className="inline-flex h-11 min-w-[96px] items-center justify-center rounded-2xl border border-red-100 bg-white px-4 text-sm font-semibold text-red-600 transition hover:border-red-200 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })
                )}
            </div>
        </section>
    );

    if (slideshow) {
        return (
            <div className="fixed inset-0 z-[80] overflow-auto bg-white">
                <Slideshow
                    transcript={slideshow.transcript}
                    title={slideshow.title}
                    version={slideshow.version}
                    language={slideshow.language}
                    description={slideshow.description}
                    exclusiveSharedRecordings={getSharedRecordingsExceptVersion(slideshow.version)}
                    onClose={closeSlideshow}
                    onSharedRecording={handleSharedRecording}
                    onUnsharedRecording={handleUnsharedRecording}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/25">
            <header className="shrink-0 border-b border-blue-100/80 bg-white/95 px-6 py-5 shadow-sm backdrop-blur-xl">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-4">
                        <button
                            type="button"
                            onClick={() => successHandler(null)}
                            className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            ← Back
                        </button>
                        <div className="min-w-0">
                            <p className="mb-1 text-xs font-black uppercase tracking-[0.22em] text-blue-600">Narration text</p>
                            <h1 className="truncate text-2xl font-bold tracking-tight text-slate-950">
                                {presentation_title.title}
                            </h1>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm">
                            {presentation_title.language}
                        </span>
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm">
                            {sortedFiles.length} version{sortedFiles.length === 1 ? "" : "s"}
                        </span>
                        {savedDescription && (
                            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 shadow-sm">
                                Description added
                            </span>
                        )}
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm">
                            Updated {formatDate(latestDate)}
                        </span>
                    </div>
                </div>
            </header>

            <main className="min-h-0 flex-1 overflow-y-auto px-6 py-7">
                <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
                    {error && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3" role="alert">
                            <p className="text-sm font-medium text-red-700">{error}</p>
                        </div>
                    )}

                    {!hasVersions && renderTranscriptInputSection()}
                    {renderSavedVersionsSection()}
                    {renderDescriptionSection()}
                    {hasVersions && renderTranscriptInputSection()}
                </div>
            </main>

            {isEditorOpen && editingFile && (
                <TranscriptEditorOverlay
                    file={editingFile}
                    presentationTitle={presentation_title}
                    onClose={closeEditor}
                    onSaved={getFiles}
                    fetchWithAuth={fetchWithAuth}
                />
            )}

            {versionToDelete && (
                <DeleteVersionDialog
                    file={versionToDelete}
                    sharingState={getVersionSharingState(versionToDelete)}
                    onCancel={cancelDeleteVersion}
                    onConfirmDelete={() => {
                        const sharingState = getVersionSharingState(versionToDelete);
                        confirmDeleteVersion(sharingState?.isShared ? "delete-and-unshare" : "transcript-only");
                    }}
                    isDeleting={isDeletingVersion}
                    deletingMode={deletingVersionMode}
                />
            )}
        </div>
    );
};

export default PresentationTitle;